import { Alert, Linking } from 'react-native';
import Translations from 'src/core/utils/Translations';
import ClipboardService from 'src/data/services/ClipboardService';
import CrashlyticsService from 'src/data/services/CrashlyticsService';
import ValidationService from 'src/data/services/ValidationService';

const TAG = '[LinkingService] ->';

class LinkingService {
	private isNothingToOpen(url: string): boolean {
		return url.trim() === '' || url.startsWith('#') || ValidationService.protocolOf(url) === null;
	}

	private redact(url: string): string {
		try {
			const { origin, pathname } = new URL(url);

			return `${origin}${pathname}`;
		} catch {
			return '[unparsable url]';
		}
	}

	private refuseUrl({ url }: { url: string }) {
		console.warn(`${TAG} refused url with a disallowed scheme:`, this.redact(url));

		CrashlyticsService.captureEvent({
			message: 'Url refused: scheme not allowed.',
			extra: { url: this.redact(url) },
		});

		Alert.alert(Translations.urlCouldNotBeOpenedTitle, Translations.urlCouldNotBeOpenedDescription, [
			{ text: Translations.copyUrl, onPress: () => ClipboardService.copyToClipboard({ text: url }) },
			{ text: Translations.okay },
		]);
	}

	openUrlExternally({ url }: { url: string }) {
		if (this.isNothingToOpen(url)) {
			return;
		}

		if (!ValidationService.isOpenableUrl(url)) {
			this.refuseUrl({ url });

			return;
		}

		Linking.openURL(url)
			// Native resolves a boolean, web resolves nothing at all: only an explicit false is a refusal.
			.then((supported: unknown) => {
				if (supported === false) {
					console.warn(`${TAG} Linking.openURL failed:`, this.redact(url));

					Alert.alert(Translations.urlCouldNotBeOpenedTitle, Translations.urlCouldNotBeOpenedDescription, [
						{ text: Translations.copyUrl, onPress: () => ClipboardService.copyToClipboard({ text: url }) },
						{ text: Translations.okay },
					]);

					CrashlyticsService.captureEvent({
						message: 'Url could not be opened externally.',
						extra: { url: this.redact(url) },
					});
				}
			})
			// The OS repeats the full url in its error message, so the error itself stays out of the report.
			.catch((error: unknown) => {
				console.warn(`${TAG} Linking.openURL rejected:`, this.redact(url), error);

				CrashlyticsService.captureEvent({
					message: 'Linking.openURL rejected.',
					extra: { url: this.redact(url) },
				});
			});
	}
}

export default new LinkingService();
